import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import {
  MessageCircle,
  ChevronLeft,
  Copy,
  HelpCircle,
  X,
  Trash2,
} from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";

const API_URL = import.meta.env.VITE_API_URL;

// ✅ Status dropdown options (keep here at the top)
const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "in_progress", label: "In progress" },
  { value: "emailed", label: "Emailed" },
  { value: "completed", label: "Completed" },
  { value: "no_reply", label: "No reply from inquirer" },
];

// --- Helper: safely parse ISO date (handles Python microseconds etc.) ---
const parseIsoDate = (value) => {
  if (!value) return null;

  let normalized = value;

  // If the string has no timezone info (no Z and no +hh:mm / -hh:mm),
  // assume it's UTC and append 'Z'
  if (!/[zZ]|[+\-]\d{2}:?\d{2}$/.test(value)) {
    // Strip microseconds if present (e.g. 2025-11-24T03:14:56.123456)
    const parts = value.split(".");
    normalized = parts[0] + "Z";
  }

  const d = new Date(normalized);
  if (Number.isNaN(d.getTime())) return null;
  return d;
};

// Format the centre message block
const formatMessage = (enquiry) => {
  if (!enquiry) return null;

  return (
    <div className="text-sm text-gray-700">
      {/* Property block */}
      <div className="mb-4">
        <div>Property Enquired:</div>

        <div>
          <span>Property name: </span>
          <strong className="font-semibold">
            {enquiry.propertyTitle || enquiry.accommodationTitle}
          </strong>
        </div>

        <div>
          <span>Location: </span>
          <strong className="font-semibold">
            {enquiry.location || enquiry.accommodationLocation || "Green Point"}
          </strong>
        </div>

        <div>
          <span>Type: </span>
          <strong className="font-semibold">
            {enquiry.type || enquiry.accommodationType || "House"}
          </strong>
        </div>

        {/* ✅ Status dropdown */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-2">
          <span>Status: </span>

          <select
            className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white w-full sm:w-auto"
            value={enquiry.status || "new"}
            onChange={(e) => enquiry.onStatusChange?.(e.target.value)}>
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Message content */}
      <div className="mb-6 whitespace-pre-wrap">{enquiry.message}</div>
    </div>
  );
};

// Helper – "4m ago" etc.
const formatTimeAgo = (isoString) => {
  const date = parseIsoDate(isoString);
  if (!date) return "";

  const now = new Date();
  const diffMs = now - date;
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return "just now";

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

// Build mailto link for "Reply via Email"
const buildMailtoLink = (enquiry) => {
  if (!enquiry) return "";

  const to = encodeURIComponent(enquiry.email || "");
  const subject = encodeURIComponent(
    `Enquiry about ${enquiry.propertyTitle || "your SDA property"}`
  );

  const bodyLines = [
    `Hi ${enquiry.name || "there"},`,
    "",
    "Thanks for your enquiry about:",
    `- Property: ${enquiry.propertyTitle || ""}`,
    `- Location: ${enquiry.location || ""}`,
    "",
    "Your message:",
    "",
    "",
    "Kind regards,",
    "Nurse Assist 24/7",
  ];

  const body = encodeURIComponent(bodyLines.join("\n"));

  // Direct Gmail compose URL
  return `https://mail.google.com/mail/?view=cm&fs=1&to=${to}&su=${subject}&body=${body}`;
};

function Enquiry() {
  const [activeId, setActiveId] = useState(null);
  const [mobileView, setMobileView] = useState("list"); // "list" | "detail"
  const [filter, setFilter] = useState("all"); // "all" | "unread"
  const [searchTerm, setSearchTerm] = useState("");
  const [openInquiryGuide, setOpenInquiryGuide] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const queryClient = useQueryClient();

  const mapInquiries = (data) => {
    return (data?.inquiries || []).map((i) => ({
      id: i.id,
      name: i.name,
      email: i.email,
      phone: i.phone,
      message: i.message,
      status: i.status || "new",
      date: i.created_at || i.createdAt || i.created || i.date,
      propertyTitle:
        i.propertyTitle ||
        i.property_name ||
        i.propertyName ||
        i.accommodationTitle ||
        i.accommodation_title ||
        "",
      location:
        i.location ||
        i.property_location ||
        i.accommodationLocation ||
        i.accommodation_location ||
        "",
      type:
        i.type ||
        i.property_type ||
        i.accommodationType ||
        i.accommodation_type ||
        "",
    }));
  };

  const fetchInquiries = async () => {
    const token = localStorage.getItem("accessToken");
    if (!token) throw new Error("No access token found");

    const res = await fetch(`${API_URL}/api/sdaowner/inquiries`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.message || "Failed to load inquiries");
    }

    return mapInquiries(data);
  };

  const { data: inquiries = [], isLoading } = useQuery({
    queryKey: ["inquiries"],
    queryFn: fetchInquiries,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!inquiries.length) {
      setActiveId(null);
      return;
    }

    setActiveId((prev) => {
      if (!prev) return inquiries[0].id;

      const match = inquiries.find((inq) => inq.id === prev);
      return match ? prev : inquiries[0].id;
    });
  }, [inquiries]);

  // ✅ Update status helper (used by dropdown)
  const updateInquiryStatus = async (id, nextStatus) => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) return;

      const res = await fetch(`${API_URL}/api/sdaowner/inquiries/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: nextStatus }),
      });

      if (!res.ok) {
        console.error("Failed to update inquiry status");
        return;
      }

      queryClient.setQueryData(["inquiries"], (prev) => {
        const list = Array.isArray(prev) ? prev : [];
        return list.map((inq) =>
          inq.id === id
            ? { ...inq, status: (nextStatus || "").trim().toLowerCase() }
            : inq
        );
      });

      queryClient.invalidateQueries({ queryKey: ["inquiries"] });

      // refresh badge
      window.dispatchEvent(new CustomEvent("inquiry-status-changed"));
    } catch (err) {
      console.error("Error updating inquiry status:", err);
    }
  };

  // ✅ Delete inquiry helper (backend allows delete regardless of status)
  const deleteInquiry = async (id) => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) return;

      const res = await fetch(`${API_URL}/api/sdaowner/inquiries/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        alert(data.message || "Failed to delete inquiry");
        return;
      }

      queryClient.setQueryData(["inquiries"], (prev) => {
        const list = Array.isArray(prev) ? prev : [];
        const next = list.filter((inq) => inq.id !== id);

        if (activeId === id) {
          setActiveId(next.length > 0 ? next[0].id : null);
        }

        return next;
      });

      queryClient.invalidateQueries({ queryKey: ["inquiries"] });

      // refresh badge
      window.dispatchEvent(new CustomEvent("inquiry-status-changed"));
    } catch (err) {
      console.error("Error deleting inquiry:", err);
    }
  };

  // Mark as read (when opening a NEW inquiry)
  const markAsRead = async (id) => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) return;

      const res = await fetch(`${API_URL}/api/sdaowner/inquiries/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: "in_progress" }),
      });

      if (!res.ok) {
        console.error("Failed to mark inquiry as read");
        return;
      }

      queryClient.setQueryData(["inquiries"], (prev) => {
        const list = Array.isArray(prev) ? prev : [];
        return list.map((inq) =>
          inq.id === id ? { ...inq, status: "in_progress" } : inq
        );
      });

      queryClient.invalidateQueries({ queryKey: ["inquiries"] });

      window.dispatchEvent(new CustomEvent("inquiry-status-changed"));
    } catch (err) {
      console.error("Error marking inquiry as read:", err);
    }
  };

  // 1) Filter by All / Unread
  const statusFiltered =
    filter === "all"
      ? inquiries
      : inquiries.filter((inq) => (inq.status || "").toLowerCase() === "new");

  // 2) Filter by search term
  const lowerSearch = searchTerm.trim().toLowerCase();
  const filteredInquiries = lowerSearch
    ? statusFiltered.filter((inq) => {
        const haystack = (
          (inq.name || "") +
          " " +
          (inq.email || "") +
          " " +
          (inq.message || "") +
          " " +
          (inq.propertyTitle || "") +
          " " +
          (inq.location || "")
        ).toLowerCase();
        return haystack.includes(lowerSearch);
      })
    : statusFiltered;

  // Active enquiry
  const active =
    filteredInquiries.find((e) => e.id === activeId) ||
    filteredInquiries[0] ||
    null;

  // If active enquiry disappears when switching filter/search, select first in filtered list
  useEffect(() => {
    if (!filteredInquiries.length) return;

    const stillVisible = filteredInquiries.some((inq) => inq.id === activeId);
    if (!stillVisible) {
      setActiveId(filteredInquiries[0].id);
    }
  }, [filteredInquiries, activeId]);

  // Pre-format header date/time
  const activeDateObj = active ? parseIsoDate(active.date) : null;
  const activeDateLabel = activeDateObj ? activeDateObj.toLocaleString() : "";

  return (
    <div className="min-h-screen">
      {/* Main content: two columns */}
      <div className="grid grid-cols-1 md:grid-cols-[360px_1fr] gap-6 md:gap-8 md:h-[calc(100vh-10rem)]">
        {/* LEFT COLUMN */}
        <Card
          className={`rounded-2xl overflow-hidden ${
            mobileView === "detail" ? "hidden md:block" : "block"
          }`}>
          <CardContent className="p-6 flex flex-col h-full">
            {/* Pills + Guide */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex gap-3 flex-1">
                <Button
                  className={`flex-1 px-6 py-2 rounded-full ${
                    filter === "all"
                      ? "bg-[#D2138C] text-white"
                      : "bg-pink-100 text-[#6e5358]"
                  }`}
                  onClick={() => setFilter("all")}>
                  All
                </Button>

                <Button
                  className={`flex-1 px-6 py-2 rounded-full ${
                    filter === "unread"
                      ? "bg-[#D2138C] text-white"
                      : "bg-pink-100 text-[#6e5358]"
                  }`}
                  onClick={() => setFilter("unread")}>
                  Unread
                </Button>
              </div>

              <button
                type="button"
                onClick={() => setOpenInquiryGuide(true)}
                className="ml-3 h-10 w-10 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition shrink-0"
                aria-label="Inquiry guide"
                title="Inquiry guide">
                <HelpCircle className="h-5 w-5 text-[#D2138C]" />
              </button>
            </div>

            {/* Search */}
            <div className="mb-4">
              <Input
                placeholder="Search enquiries..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Scrollable list */}
            <ScrollArea className="flex-1 overflow-auto pr-2">
              <ul className="space-y-4">
                {filteredInquiries.map((enq) => {
                  const isActive = enq.id === activeId;
                  return (
                    <li
                      key={enq.id}
                      onClick={() => {
                        setActiveId(enq.id);
                        setMobileView("detail"); // ✅ mobile: go to details
                        if ((enq.status || "").toLowerCase() === "new") {
                          markAsRead(enq.id);
                        }
                      }}
                      className={`flex items-start gap-4 p-4 rounded-xl cursor-pointer transition-shadow ${
                        isActive
                          ? "bg-pink-50 shadow-inner"
                          : "hover:bg-pink-50/60"
                      }`}>
                      <div>
                        <Avatar className="h-12 w-12 bg-pink-100">
                          <AvatarFallback>
                            <MessageCircle className="w-5 h-5 text-pink-600" />
                          </AvatarFallback>
                        </Avatar>
                      </div>

                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <div className="font-medium">{enq.name}</div>
                          <div className="text-xs text-gray-500">
                            {formatTimeAgo(enq.date)}
                          </div>
                        </div>

                        <p className="text-sm text-gray-600 mt-1">
                          {enq.message && enq.message.length > 80
                            ? enq.message.slice(0, 80) + "..."
                            : enq.message}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* RIGHT COLUMN */}
        <Card
          className={`p-4 md:p-8 h-full flex flex-col min-h-0 ${
            mobileView === "list" ? "hidden md:flex" : "flex"
          }`}>
          {active ? (
            <>
              {/* header row with avatar, name/email and date */}
              <div className="flex items-start gap-3 md:gap-4">
                {/* ✅ Mobile back button */}
                <Button
                  variant="outline"
                  className="md:hidden h-10 w-10 p-0 flex items-center justify-center shrink-0"
                  onClick={() => setMobileView("list")}
                  aria-label="Back to list"
                  title="Back">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="shrink-0">
                  <Avatar className="h-12 w-12 bg-pink-100">
                    <AvatarFallback>
                      <MessageCircle className="w-5 h-5 text-pink-600" />
                    </AvatarFallback>
                  </Avatar>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-lg leading-tight">
                        {active.name}
                      </div>

                      <div className="text-sm text-gray-500 break-all">
                        {active.email}
                      </div>

                      <div className="text-xs text-gray-400">Guest</div>

                      {/* ✅ Date under name on mobile */}
                      <div className="text-sm text-gray-500 mt-2 md:hidden">
                        {activeDateLabel}
                      </div>
                    </div>

                    <div className="flex items-center gap-3 md:justify-end">
                      {/* ✅ Date stays right on desktop */}
                      <div className="text-sm text-gray-500 hidden md:block">
                        {activeDateLabel}
                      </div>

                      {/* ✅ SHOW DELETE ICON ANYTIME */}
                      <Button
                        variant="outline"
                        className="hidden md:flex h-10 w-10 p-0 items-center justify-center text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => {
                          setDeleteTargetId(active.id);
                          setOpenDeleteModal(true);
                        }}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 my-4 md:my-6" />

              {/* main detail body */}
              <ScrollArea className="flex-1 overflow-auto pr-4 min-w-0">
                {formatMessage({
                  ...active,
                  onStatusChange: (nextStatus) =>
                    updateInquiryStatus(active.id, nextStatus),
                })}

                {/* CTA area */}
                <div className="mt-6 border-t border-gray-200 pt-6 text-sm text-gray-600">
                  <div className="mb-4">
                    <div className="mb-1 text-gray-500">Guest’s Details:</div>
                    <div>
                      <span>Name: </span>
                      <strong>{active.name}</strong>
                    </div>
                    <div>
                      <span>Email: </span>
                      <strong>{active.email}</strong>
                    </div>
                    <div className="mb-4">
                      <span>Phone: </span>
                      <strong>{active.phone}</strong>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-2">
                      <Button
                        variant="outline"
                        className="flex items-center gap-2 w-full sm:w-auto"
                        onClick={() => {
                          const href = buildMailtoLink(active);
                          if (href) {
                            window.open(href, "_blank", "noopener,noreferrer");
                          }
                        }}>
                        <ChevronLeft className="w-4 h-4" />
                        Reply via email
                      </Button>

                      <Button
                        variant="outline"
                        className="flex items-center gap-2 w-full sm:w-auto"
                        onClick={() =>
                          navigator.clipboard.writeText(active.phone || "")
                        }>
                        <Copy className="w-4 h-4" />
                        Copy {active.phone}
                      </Button>
                    </div>

                    {/* ✅ Mobile delete button (below copy) */}
                    <div className="mt-3 md:hidden">
                      <Button
                        variant="outline"
                        className="w-full flex items-center justify-center gap-2 text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => {
                          setDeleteTargetId(active.id);
                          setOpenDeleteModal(true);
                        }}>
                        <Trash2 className="w-4 h-4" />
                        Delete inquiry
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="h-12" />
              </ScrollArea>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
              No enquiries to display.
            </div>
          )}
        </Card>
      </div>

      {openInquiryGuide && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            onClick={() => setOpenInquiryGuide(false)}
            aria-label="Close guide"
          />

          {/* Modal */}
          <div className="relative z-10 w-[95%] max-w-xl rounded-2xl bg-white shadow-lg">
            <div className="flex items-center justify-between border-b px-5 py-4">
              <h2 className="text-base font-semibold text-gray-900">
                Inquiry guide
              </h2>

              <button
                type="button"
                onClick={() => setOpenInquiryGuide(false)}
                className="h-9 w-9 rounded-full hover:bg-gray-100 flex items-center justify-center"
                aria-label="Close">
                <X className="h-5 w-5 text-gray-600" />
              </button>
            </div>

            <div className="px-5 py-4 text-sm text-gray-700 space-y-3">
              <p>Use this page to review and respond to new enquiries.</p>

              <ul className="list-disc pl-5 space-y-1">
                <li>
                  Use <span className="font-medium">All</span> to see every
                  enquiry.
                </li>
                <li>
                  Use <span className="font-medium">Unread</span> to focus on
                  new enquiries.
                </li>
                <li>
                  Use <span className="font-medium">Search</span> to find
                  enquiries by name, email, property, or location.
                </li>
                <li>
                  Click an enquiry on the left to view details on the right.
                </li>
                <li>New enquiries are marked as read when opened.</li>
                <li>
                  Use the Status dropdown to track progress (Emailed, Completed,
                  etc.).
                </li>
                <li>
                  You can delete an enquiry anytime using the Delete Button.
                </li>
              </ul>

              <div className="text-xs text-gray-500">
                Tip: Use “Reply via email” or copy the phone number for follow
                up.
              </div>
            </div>

            <div className="border-t px-5 py-4 flex justify-end">
              <button
                type="button"
                onClick={() => setOpenInquiryGuide(false)}
                className="rounded-xl bg-[#D2138C] px-4 py-2 text-sm font-medium text-white hover:opacity-90 transition">
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={openDeleteModal} onOpenChange={setOpenDeleteModal}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this inquiry?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the inquiry and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel
              className="rounded-xl"
              onClick={() => {
                setOpenDeleteModal(false);
                setDeleteTargetId(null);
              }}>
              Cancel
            </AlertDialogCancel>

            <AlertDialogAction
              className="rounded-xl bg-red-600 hover:bg-red-700"
              onClick={async () => {
                if (!deleteTargetId) return;
                await deleteInquiry(deleteTargetId);
                setOpenDeleteModal(false);
                setDeleteTargetId(null);
              }}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default Enquiry;
